<script setup lang="ts">
import {
  GalleryVerticalEnd as GalleryVerticalEndIcon,
  Library as LibraryIcon,
  Pin as PinIcon,
  Search as SearchIcon,
} from '@lucide/vue'
import { computed, ref, watch } from 'vue'

import UserMenu from '@/components/auth/UserMenu.vue'
import { useSafeAreaInsets } from '@/services/safeAreaService'
import { useUiStore } from '@/stores/uiStore'
import { Z_INDEX } from '@/utils/zIndex'

// The persistent mobile bottom navigation. HIG-aligned: every slot is a destination. The
// create action ("+") is a separate floating FAB (see WorkspaceMapContainer). Rendered as
// a sibling of the bottom-sheet panel so its visibility is decoupled from the sheet.
type MobileTab = 'collections' | 'library' | 'pinned' | 'search' | null
type MainModeTab = 'library' | 'collections' | 'pinned'

const props = defineProps<{
  activeTab?: MobileTab
}>()

const emit = defineEmits<{
  (e: 'collections'): void
  (e: 'library'): void
  (e: 'pinned'): void
}>()

const uiStore = useUiStore()
const safeAreaInsets = useSafeAreaInsets()
const highlightedMainMode = ref<MainModeTab>('pinned')

// A frosted glass pill floating above the home indicator, the same width as the bottom-sheet
// card (left-3/right-3). It stays a standalone, fully-rounded translucent pill at ALL times —
// over the bare map AND with any sheet open. The sheets bottom-anchor just above the bar (see
// each sheet's `+ 64px + 8px` offset), leaving an intentional sliver of map behind/around the
// bar so the glass always reads as glass (Apple Maps / iOS 26 HIG floating-toolbar language),
// instead of fusing into one opaque white card.
const bottomOffset = computed(() => `max(${safeAreaInsets.value.bottom}, 10px)`)
const shellClass = 'rounded-2xl border border-white/60 bg-white/70'

const mainModeIndex = computed(() => {
  switch (highlightedMainMode.value) {
    case 'library':
      return 0
    case 'collections':
      return 1
    case 'pinned':
      return 2
    default:
      return 2
  }
})
const mainModeHighlightStyle = computed(() => ({
  opacity: 1,
  transform: `translateX(${mainModeIndex.value * 100}%)`,
}))

watch(
  () => props.activeTab,
  (activeTab) => {
    if (activeTab === 'library' || activeTab === 'collections' || activeTab === 'pinned') {
      highlightedMainMode.value = activeTab
    }
  },
  { immediate: true },
)

const isMainModeHighlighted = (tab: MainModeTab) => highlightedMainMode.value === tab

// Active-tab affordance (Instagram / Signal / iOS 26 HIG): the selected tab gets a filled
// rounded "lozenge" behind its icon + label, not just a colour change. `tabSlot` is the full-cell
// tap target (≥44px); `tabPill` is the inner lozenge that carries the highlight.
const tabSlot = 'flex h-full w-full items-center justify-center hover:cursor-pointer'
const tabPill =
  'relative z-10 flex w-full flex-col items-center justify-center gap-0.5 rounded-2xl py-1.5 text-[11px] font-medium transition-colors duration-200'
const tabPillActive = 'text-emerald-700 font-semibold'
const utilityTabPillActive = 'bg-emerald-100 text-emerald-700 font-semibold'
const tabPillInactive = 'text-gray-500'

// Opening search reuses the existing MapSmartSearch pill: it watches
// uiStore.isSmartSearchOpen and focuses/loads when it flips true. Collapse the sheet so
// the search overlay isn't hidden behind it.
const openSearch = () => {
  // Tapping the active Search tab again closes it (toggle).
  if (uiStore.isSmartSearchOpen) {
    uiStore.closeSmartSearch()
    return
  }
  uiStore.openSmartSearch()
  uiStore.collapseMobileBottomPanel()
}

const dispatchWindowEvent = (name: string, detail?: unknown) => {
  window.dispatchEvent(
    detail === undefined ? new CustomEvent(name) : new CustomEvent(name, { detail }),
  )
}
</script>

<template>
  <nav
    class="fixed left-3 right-3 shadow-[0_8px_30px_rgba(15,23,42,0.14)] backdrop-blur-xl backdrop-saturate-150"
    :class="shellClass"
    :style="{
      zIndex: Z_INDEX.MOBILE_TAB_BAR,
      bottom: bottomOffset,
    }"
    role="tablist"
    aria-label="Primary mobile navigation"
  >
    <div class="flex h-16 items-center gap-1 px-2">
      <div class="relative grid h-full min-w-0 flex-[3] grid-cols-3 gap-1">
        <div
          class="pointer-events-none absolute inset-y-1.5 left-0 w-1/3 rounded-2xl bg-emerald-100 transition-[transform,opacity] duration-300 ease-out"
          :style="mainModeHighlightStyle"
          aria-hidden="true"
        ></div>

        <!-- Library -->
        <button
          type="button"
          role="tab"
          :aria-selected="props.activeTab === 'library'"
          :class="tabSlot"
          aria-label="Library: my activities and routes"
          @click="emit('library')"
        >
          <span
            :class="[tabPill, isMainModeHighlighted('library') ? tabPillActive : tabPillInactive]"
          >
            <LibraryIcon class="h-5 w-5" />
            <span class="max-w-full truncate">Library</span>
          </span>
        </button>

        <!-- Collections -->
        <button
          type="button"
          role="tab"
          :aria-selected="props.activeTab === 'collections'"
          :class="tabSlot"
          aria-label="Collections"
          @click="emit('collections')"
        >
          <span
            :class="[
              tabPill,
              isMainModeHighlighted('collections') ? tabPillActive : tabPillInactive,
            ]"
          >
            <GalleryVerticalEndIcon class="h-5 w-5" />
            <span class="max-w-full truncate">Collections</span>
          </span>
        </button>

        <!-- Pinned -->
        <button
          type="button"
          role="tab"
          :aria-selected="props.activeTab === 'pinned'"
          :class="tabSlot"
          aria-label="Pinned"
          @click="emit('pinned')"
        >
          <span
            :class="[tabPill, isMainModeHighlighted('pinned') ? tabPillActive : tabPillInactive]"
          >
            <PinIcon class="h-5 w-5" />
            <span class="max-w-full truncate">Pinned</span>
          </span>
        </button>
      </div>

      <div class="mx-1 h-8 w-px shrink-0 bg-slate-300/70" aria-hidden="true"></div>

      <!-- Search -->
      <button
        type="button"
        role="tab"
        :aria-selected="props.activeTab === 'search'"
        :class="[tabSlot, 'flex-1']"
        aria-label="Search places, collections, activities, and routes"
        @click="openSearch"
      >
        <span
          :class="[tabPill, props.activeTab === 'search' ? utilityTabPillActive : tabPillInactive]"
        >
          <SearchIcon class="h-5 w-5" />
          <span class="max-w-full truncate">Search</span>
        </span>
      </button>

      <!-- Profile -->
      <div class="flex h-full min-w-0 flex-1 items-stretch justify-center">
        <UserMenu
          variant="mobile-tab"
          @open-auth-modal="dispatchWindowEvent('open-auth-modal')"
          @open-profile-edit="dispatchWindowEvent('open-auth-modal', { view: 'profile-edit' })"
          @open-sync-dialog="dispatchWindowEvent('open-sync-status')"
          @open-static-site="(pageId: string) => dispatchWindowEvent('open-static-site', pageId)"
        />
      </div>
    </div>
  </nav>
</template>
